import { useEffect, useState, type ReactNode } from 'react'

import { ChevronLeftIcon, ChevronRightIcon } from '~/components/icons'
import { cn } from '~/lib/cn'

/**
 * A row of cards that runs off the side of the page, with the pointer's way of moving it.
 *
 * The row itself is an ordinary scroller — a finger swipes it, a trackpad swipes it, tabbing
 * into a card brings that card into view — so the controls this adds are for the one pointer
 * that has no other way of moving it. A mouse wheel over a row this shape scrolls the page
 * behind it, and shift-wheel is a secret; without the controls a mouse simply cannot reach the
 * far end of the row. That is why they are the pointer's alone: a finger already has the row,
 * and would only be reading around two buttons it can never need.
 *
 * They are not stops on the way through the page either, and are not announced. Everything the
 * row holds leads somewhere and takes the focus itself, so a control that only moves the
 * viewport is one more thing to pass through on the way to the same cards.
 *
 * No bar is drawn under the row. What says there is more to the right is the card the edge cuts
 * off, which every way of reading the page can see, where a bar 8px tall aimed at a pointer is
 * both hard to grab and invisible to the trackpad that never needs it.
 */
export function SidewaysRow({
  as: Track = 'div',
  label,
  role,
  className,
  children,
}: {
  /** A list of things is a list; the default is for rows whose cards are not one. */
  as?: 'div' | 'ul'
  label?: string
  role?: 'group'
  /** The spacing the row's own cards ask for — the gap between them, and any room under. */
  className?: string
  children: ReactNode
}) {
  const [track, setTrack] = useState<HTMLElement | null>(null)
  const [reach, setReach] = useState({ back: false, on: false })

  useEffect(() => {
    if (track === null) {
      return undefined
    }
    const measure = () => {
      const room = roomLeft(track)
      const back = room.back > EDGE
      const on = room.on > EDGE
      // Only a flipped answer is worth a render: this runs on every scrolled pixel of the row.
      setReach((held) => (held.back === back && held.on === on ? held : { back, on }))
    }
    /*
     * Three things change where a row can go: it moves, the page around it changes width, or
     * cards arrive in it. The first two are the scroll and the resize; the third is neither,
     * because a row that gains a card is a row whose own box never changed — so the cards are
     * watched as well. Observing is also what takes the first measurement, since a fresh
     * observer reports what it is given straight away.
     */
    const size = new ResizeObserver(measure)
    size.observe(track)
    const cards = new MutationObserver(measure)
    cards.observe(track, { childList: true })
    track.addEventListener('scroll', measure, { passive: true })
    return () => {
      size.disconnect()
      cards.disconnect()
      track.removeEventListener('scroll', measure)
    }
  }, [track])

  const move = (direction: 1 | -1) => {
    if (track === null) {
      return
    }
    const room = roomLeft(track)
    // A stride, or the rest of the way where the rest of the way is shorter — so the last
    // press of a row lands on the end itself rather than somewhere near it.
    const stride = Math.min(
      Math.round(track.clientWidth * STRIDE),
      Math.ceil(direction === 1 ? room.on : room.back),
    )
    track.scrollBy({
      left: stride * direction,
      behavior: withoutMotion() ? 'auto' : 'smooth',
    })
  }

  return (
    <div className="group/row relative">
      <Track
        ref={setTrack}
        role={role}
        aria-label={label}
        className={cn(
          // Contained, so a trackpad running off the end of the row is not the browser
          // being asked to go back a page.
          'flex overflow-x-auto overscroll-x-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
          className,
        )}
      >
        {children}
      </Track>
      {reach.back && (
        <Step
          side="back"
          onPress={() => {
            move(-1)
          }}
        />
      )}
      {reach.on && (
        <Step
          side="on"
          onPress={() => {
            move(1)
          }}
        />
      )}
    </div>
  )
}

/** Most of the row's width, so what stood at the edge is still on screen when it lands. */
const STRIDE = 0.8

/*
 * A row within a hair of an end is at that end: browsers do not land on round numbers, and
 * nothing narrower than this is worth travelling to.
 */
const EDGE = 8

/**
 * How much row there is left to see each way — behind its left edge, and past its right one.
 *
 * The far end is where the last card ends, not where the row says its scrollable width ends.
 * The two are not the same: a card wears decoration that reaches past its own box, and the
 * moment the pointer rests on the last card that decoration adds a pixel or two to the width
 * the row reports. A press that lands at the end leaves the pointer standing on exactly that
 * card — so measured against the reported width, arriving at the end is itself what says the
 * row has somewhere left to go, and the control comes back up over a row that cannot move.
 * A card's own box does not swell, so it is what the end is read from.
 */
function roomLeft(track: HTMLElement): { back: number; on: number } {
  const last = track.lastElementChild
  return {
    back: track.scrollLeft,
    on:
      last === null ? 0 : last.getBoundingClientRect().right - track.getBoundingClientRect().right,
  }
}

/** The way on, or the way back — one at each end, and only while there is somewhere to go. */
function Step({ side, onPress }: { side: 'back' | 'on'; onPress: () => void }) {
  const back = side === 'back'
  return (
    <button
      type="button"
      tabIndex={-1}
      aria-hidden
      aria-label={back ? 'Scroll left' : 'Scroll right'}
      onClick={onPress}
      className={cn(
        /*
         * Above what the page floats over its own edges — the timeline's rail rides there,
         * and a control a press cannot reach is not a control. Below the header all the
         * same, which is chrome the page scrolls under rather than chrome laid over a row.
         *
         * Carrying a surface of its own, it brightens that surface a step under the pointer
         * rather than taking the wash over it — see the web design spec, Pointer.
         */
        'absolute top-1/2 z-[25] hidden size-10 -translate-y-1/2 place-items-center rounded-full bg-surface-overlay/90 text-ink opacity-0 shadow-lg ring-1 ring-white/10 transition-opacity duration-100 group-hover/row:opacity-100 hover:bg-surface-bright pointer-fine:grid',
        back ? 'left-1' : 'right-1',
      )}
    >
      {back ? (
        <ChevronLeftIcon aria-hidden className="size-6" />
      ) : (
        <ChevronRightIcon aria-hidden className="size-6" />
      )}
    </button>
  )
}

/** Whether motion is unwanted — the same question a dialog asks before it animates. */
function withoutMotion(): boolean {
  return (
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  )
}
